import { Component, OnInit } from '@angular/core';
import { ColDef, GridApi, GridReadyEvent } from 'ag-grid-community';
import { ToolsService } from 'src/app/services/tools.service';

type WosUser = {
  id?: number;
  f_name?: string;
  m_name?: string;
  l_name?: string;
  w_email?: string;
  p_email?: string;
  admin?: number;
  create_task?: number;
  old_name?: string;
  active?: number;
  role?: number;
  title?: string;
  linkedin_link?: string;
  firebase_UID?: string;
};

@Component({
  selector: 'app-users',
  templateUrl: './users.component.html',
  styleUrls: ['./users.component.css']
})
export class UsersComponent implements OnInit {
  token = '';
  loading = false;
  errorMessage = '';
  gridApi!: GridApi;
  users: WosUser[] = [];
  rowData: WosUser[] = [];
  view: 'active' | 'inactive' = 'active';
  isEditing = false;
  editor: WosUser = this.blankUser();

  columnDefs: ColDef[] = [
    {
      headerName: '',
      colId: 'actions',
      width: 46,
      minWidth: 46,
      maxWidth: 46,
      pinned: 'right',
      suppressMenu: true,
      sortable: false,
      resizable: false,
      cellRenderer: () =>
        `<button class="btn btn-link p-0 users-row-delete" title="Deactivate">
            <i class="fas fa-fw fa-trash"></i>
         </button>`,
      onCellClicked: (p) => this.deleteUser(p.data?.id)
    },

    { headerName: 'First', field: 'f_name', width: 150, sortable: true, filter: true },
    { headerName: 'Middle', field: 'm_name', width: 120, sortable: true, filter: true },
    { headerName: 'Last', field: 'l_name', width: 170, sortable: true, filter: true },
    { headerName: 'Work Email', field: 'w_email', width: 260, sortable: true, filter: true },
    { headerName: 'Admin', field: 'admin', width: 100, sortable: true, filter: true },
    { headerName: 'Create Task', field: 'create_task', width: 130, sortable: true, filter: true },
    { headerName: 'Role', field: 'role', width: 100, sortable: true, filter: true },
    {
      headerName: 'Active',
      field: 'active',
      width: 100,
      sortable: true,
      filter: true,
      valueFormatter: (p) => Number(p.value) === 1 ? 'Yes' : 'No'
    },
    { headerName: 'Title', field: 'title', width: 200, sortable: true, filter: true },
    { headerName: 'LinkedIn', field: 'linkedin_link', width: 280, sortable: true, filter: true } 
  ];

  defaultColDef: ColDef = {
    resizable: true,
    sortable: true
  };

  constructor(private toolsService: ToolsService) {}

  ngOnInit(): void {
    const userStr = sessionStorage.getItem('wOSUser') || '{}';
    const parsedUser = JSON.parse(userStr);
    this.token = parsedUser?.stsTokenManager?.accessToken || '';

    this.loadUsers();
  }

  onGridReady(e: GridReadyEvent): void {
    this.gridApi = e.api;
  }

  setView(v: 'active' | 'inactive'): void {
    this.view = v;
    this.loadUsers();
  }

  async loadUsers(): Promise<void> {
    this.loading = true;
    this.errorMessage = '';

    try {
      const activeFlag = this.view === 'inactive' ? 0 : 1;
      const payload = { active: activeFlag };

      const res = await this.toolsService.manageUsers('list', payload, this.token);

      const rows: any[] = Array.isArray(res) ? res : (Array.isArray((res as any)?.data) ? (res as any).data : []);

      this.users = rows.map((r: any) => ({
        id: Number(r.id),
        f_name: r.f_name ?? '',
        m_name: r.m_name ?? '',
        l_name: r.l_name ?? '',
        w_email: r.w_email ?? '',
        p_email: r.p_email ?? '',
        admin: Number(r.admin ?? 0),
        create_task: Number(r.create_task ?? 0),
        old_name: r.old_name ?? '',
        active: Number(r.active ?? 0),
        role: Number(r.role ?? 0),
        title: r.title ?? '',
        linkedin_link: r.linkedin_link ?? '',
        firebase_UID: r.firebase_UID ?? r.firebase_uid ?? r.firebase_UID ?? ''
      }));

      this.rowData = this.users;
    } catch (err) {
      console.error('loadUsers failed', err);
      this.errorMessage = 'Failed to load users.';
    } finally {
      this.loading = false;
    }
  }

  openCreate(): void {
    this.isEditing = false;
    this.editor = this.blankUser();
    this.toggleModal('show');
  }

  onRowClicked(e: any): void {
    const tgt = e?.event?.target as HTMLElement | null;
    if (tgt && tgt.closest('.users-row-delete')) return;

    const row = e?.data as WosUser;
    if (!row?.id) return;

    this.isEditing = true;
    this.editor = { ...row };
    this.toggleModal('show');
  }

  async save(): Promise<void> {
    this.loading = true;
    this.errorMessage = '';

    try {
      const mode = this.isEditing ? 'edit' : 'add';

      if (!this.editor.w_email || !this.editor.w_email.trim()) {
        alert('Work email is required.');
        return;
      }

      const payload: any = {
        ...(this.isEditing ? { id: this.editor.id } : {}),
        f_name: this.editor.f_name ?? '',
        m_name: this.editor.m_name ?? '',
        l_name: this.editor.l_name ?? '',
        w_email: (this.editor.w_email ?? '').trim(),
        p_email: (this.editor.p_email ?? '').trim(),
        admin: Number(this.editor.admin ?? 0),
        create_task: Number(this.editor.create_task ?? 0),
        old_name: this.editor.old_name ?? '',
        active: Number(this.editor.active ?? 1),
        role: Number(this.editor.role ?? 1),
        title: this.editor.title ?? '',
        linkedin_link: this.editor.linkedin_link ?? ''
      };

      await this.toolsService.manageUsers(mode, payload, this.token);

      await this.loadUsers();
      this.toggleModal('hide');
    } catch (err) {
      console.error('save failed', err);
      alert('Save failed.');
    } finally {
      this.loading = false;
    }
  }

  async deleteUser(id: number): Promise<void> {
    if (!id) return;

    const ok = confirm(`Deactivate user #${id}?`);
    if (!ok) return;

    this.loading = true;

    try {
      await this.toolsService.manageUsers('delete', { id }, this.token);
      await this.loadUsers();
    } catch (err) {
      console.error('delete failed', err);
      alert('Deactivate failed.');
    } finally {
      this.loading = false;
    }
  }

  closeModal(): void {
    this.toggleModal('hide');
  }

  private blankUser(): WosUser {
    return {
      id: 0,
      f_name: '',
      m_name: '',
      l_name: '',
      w_email: '',
      p_email: '',
      admin: 0,
      create_task: 0,
      old_name: '',
      active: 1,
      role: 1,
      title: '',
      linkedin_link: ''
    };
  }

  private toggleModal(action: 'show' | 'hide'): void {
    const el = document.getElementById('usersEditorModal');
    if (!el) return;
    // @ts-ignore
    const modal = window.bootstrap?.Modal?.getOrCreateInstance(el);
    if (!modal) return;
    action === 'show' ? modal.show() : modal.hide();
  }
}
